import { useEventCallback } from '@mui/material/utils';
import { DateTime } from 'luxon';
import { ChangeEventHandler, FocusEventHandler, useMemo } from 'react';

import { DateTimeUtils } from '../utils';

import { DateTimePickerFieldProps } from './types';

type Props = Required<
  Pick<DateTimePickerFieldProps, 'onChange' | 'clearable' | 'value'>
>;

type OnSelectFunction = (date: DateTime) => void;
type OnChangeFunction = ChangeEventHandler<
  HTMLInputElement | HTMLTextAreaElement
>;
type OnBlurFunction = FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
type HandleEnterEscapeTabKeysFunction = (
  handler: () => void
) => (event: React.KeyboardEvent) => void;

type Return = {
  onDateSelect: OnSelectFunction;
  onDateChange: OnChangeFunction;
  onDateBlur: OnBlurFunction;
  onTimeSelect: OnSelectFunction;
  onTimeChange: OnChangeFunction;
  onTimeBlur: OnBlurFunction;
  onClear: () => void;
  handleEnterEscapeTabKeys: HandleEnterEscapeTabKeysFunction;
};

export const useDateTimePickerFieldEventsHandlers = ({
  onChange,
  clearable,
  value,
}: Props): Return => {
  const onDateSelect: OnSelectFunction = useEventCallback((date) => {
    onChange(
      date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISO()
    );
  });

  const onDateChange: OnChangeFunction = useEventCallback((e) => {
    onChange(DateTimeUtils.updateDateFromFormatedDate(value, e.target.value));
  });

  const onDateBlur: OnBlurFunction = useEventCallback((e) => {
    onChange(DateTimeUtils.updateDateFromFormatedDate(value, e.target.value));
  });

  const onTimeSelect: OnSelectFunction = useEventCallback((date) => {
    onChange(DateTimeUtils.updateTimeFromDateTime(value, date));
  });

  const onTimeChange: OnChangeFunction = useEventCallback((e) => {
    onChange(DateTimeUtils.updateTimeFromFormatedTime(value, e.target.value));
  });

  const onTimeBlur: OnBlurFunction = useEventCallback((e) => {
    onChange(DateTimeUtils.updateTimeFromFormatedTime(value, e.target.value));
  });

  const onClear = useEventCallback(() => {
    onChange(null);
  });

  const handleEnterEscapeTabKeys: HandleEnterEscapeTabKeysFunction = (
    handler
  ) => {
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (['Enter', 'Escape', 'Tab'].includes(event.key)) {
        handler();
      }
    };

    return handleKeyDown;
  };

  return useMemo(() => {
    return {
      onDateSelect,
      onDateChange,
      onDateBlur,
      onTimeSelect,
      onTimeChange,
      onTimeBlur,
      onClear: clearable ? onClear : undefined,
      handleEnterEscapeTabKeys,
    };
  }, [
    onClear,
    onDateBlur,
    onDateChange,
    onDateSelect,
    onTimeBlur,
    onTimeChange,
    onTimeSelect,
    clearable,
  ]);
};
